import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AiAssistantPanel } from "@/components/AiAssistantPanel";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useLocation } from "wouter";
import { useAuth, UserButton } from "@clerk/react";
import {
  getAdminStats, adminListDebts, adminListDonations, adminListPools,
  adminListPayouts, adminApprovePayout, adminBlockPayout, adminListAuditLogs,
  createPool, adminUpdateDebt, getVersion, updatePool, adminSeedPools,
  getAdminInbox, markInboxMessageRead, markAllInboxRead, replyToInboxMessage,
  getAdminBoardOfficers, activateBoardRole, deactivateBoardRole, searchAdminUsers, getAdminAllUsers, deleteAdminUser,
  type CreatePoolBody, type Pool, type InboxMessage, type BoardOfficer, type AdminUserRow,
} from "@workspace/api-client-react";
import {
  ArrowLeft, Users, DollarSign, BarChart3, ShieldCheck, Loader2,
  CheckCircle, XCircle, ScrollText, Plus, Zap, AlertTriangle, Flag, FolderOpen, Send, FileText, Receipt, Activity, ShieldAlert, ClipboardList, Mail, MailOpen, Reply, type LucideIcon,
  Key, UserPlus, UserMinus, Copy, Check, Shield, Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import AdminProfileReview from "@/components/debt-profiles/AdminProfileReview";

function StatTile({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: LucideIcon; color: string }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CATEGORY_OPTIONS = [
  { value: "medical", label: "Medical" },
  { value: "student", label: "Student Debt" },
  { value: "credit_card", label: "Credit & Consumer" },
  { value: "auto", label: "Auto & Transportation" },
  { value: "housing", label: "Housing & Utilities" },
  { value: "legal", label: "Legal & Civil" },
  { value: "family", label: "Family & Childcare" },
  { value: "veterans", label: "Veterans & Military" },
  { value: "small_business", label: "Small Business" },
  { value: "misc", label: "Miscellaneous" },
];

const CATEGORY_COLORS: Record<string, string> = {
  medical: "bg-red-100 text-red-700",
  student: "bg-blue-100 text-blue-700",
  credit_card: "bg-orange-100 text-orange-700",
  auto: "bg-yellow-100 text-yellow-700",
  housing: "bg-green-100 text-green-700",
  legal: "bg-purple-100 text-purple-700",
  family: "bg-pink-100 text-pink-700",
  veterans: "bg-indigo-100 text-indigo-700",
  small_business: "bg-teal-100 text-teal-700",
  misc: "bg-gray-100 text-gray-700",
};

const EMPTY_POOL_FORM = { name: "", theme: "", description: "", threshold: "", category: "", icon: "", creditorName: "" };

function UserActivateRow({
  user,
  activatingId,
  activatingRole,
  isPending,
  onActivate,
  onDeactivate,
  deactivatePending,
  onDelete,
}: {
  user: AdminUserRow;
  activatingId: number | null;
  activatingRole: "secretary" | "treasurer" | null;
  isPending: boolean;
  onActivate: (id: number, role: "secretary" | "treasurer") => void;
  onDeactivate: (id: number) => void;
  deactivatePending: boolean;
  onDelete: (user: AdminUserRow) => void;
}) {
  const isActivatingSec = isPending && activatingId === user.id && activatingRole === "secretary";
  const isActivatingTre = isPending && activatingId === user.id && activatingRole === "treasurer";
  return (
    <div className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/20">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{user.displayName ?? user.email}</p>
        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
      </div>
      <div className="flex items-center gap-1.5 ml-3 shrink-0">
        {user.boardRole === "secretary" ? (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">Secretary</Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            disabled={isActivatingSec || isPending}
            onClick={() => onActivate(user.id, "secretary")}
          >
            {isActivatingSec ? <Loader2 className="h-3 w-3 animate-spin" /> : <><UserPlus className="h-3 w-3 mr-1" />Secretary</>}
          </Button>
        )}
        {user.boardRole === "treasurer" ? (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">Treasurer</Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
            disabled={isActivatingTre || isPending}
            onClick={() => onActivate(user.id, "treasurer")}
          >
            {isActivatingTre ? <Loader2 className="h-3 w-3 animate-spin" /> : <><UserPlus className="h-3 w-3 mr-1" />Treasurer</>}
          </Button>
        )}
        {user.boardRole && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            disabled={deactivatePending}
            onClick={() => onDeactivate(user.id)}
          >
            <UserMinus className="h-3 w-3 mr-1" />Revoke
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          title="Delete user"
          onClick={() => onDelete(user)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isSignedIn } = useAuth();

  const [createPoolOpen, setCreatePoolOpen] = useState(false);
  const [poolForm, setPoolForm] = useState(EMPTY_POOL_FORM);
  const [payoutConfirmId, setPayoutConfirmId] = useState<number | null>(null);
  const [poolCategoryFilter, setPoolCategoryFilter] = useState("all");
  const [poolStatusFilter, setPoolStatusFilter] = useState("all");
  const [poolSortBy, setPoolSortBy] = useState<"sortOrder" | "percentFunded" | "createdAt" | "threshold">("sortOrder");
  const [poolPage, setPoolPage] = useState(1);
  const POOLS_PER_PAGE = 50;
  const [editPool, setEditPool] = useState<Pool | null>(null);
  const [editForm, setEditForm] = useState({ name: "", theme: "", description: "", threshold: "", category: "", icon: "", sortOrder: "0", status: "active" as string });
  const [expandedDebtId, setExpandedDebtId] = useState<number | null>(null);
  const [verifyOverrideId, setVerifyOverrideId] = useState<number | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [rejectDebtId, setRejectDebtId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [auditFromDate, setAuditFromDate] = useState("");
  const [auditToDate, setAuditToDate] = useState("");

  const [activeTab, setActiveTab] = useState("pools");
  const [selectedMessage, setSelectedMessage] = useState<InboxMessage | null>(null);
  const [replyText, setReplyText] = useState("");
  const [inboxPage, setInboxPage] = useState(1);
  const [inboxUnreadOnly, setInboxUnreadOnly] = useState(false);

  const [boardSearchQuery, setBoardSearchQuery] = useState("");
  const [boardSearchResults, setBoardSearchResults] = useState<AdminUserRow[]>([]);
  const [boardSearchLoading, setBoardSearchLoading] = useState(false);
  const [boardKeyModal, setBoardKeyModal] = useState<{ plainKey: string; role: string; email: string } | null>(null);
  const [boardKeyCopied, setBoardKeyCopied] = useState(false);
  const [boardActivatingId, setBoardActivatingId] = useState<number | null>(null);
  const [boardActivatingRole, setBoardActivatingRole] = useState<"secretary" | "treasurer" | null>(null);
  const [allUsersPage, setAllUsersPage] = useState(1);
  const [deleteUserTarget, setDeleteUserTarget] = useState<AdminUserRow | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["adminStats"],
    queryFn: getAdminStats,
  });

  const { data: debts } = useQuery({ queryKey: ["adminDebts"], queryFn: adminListDebts });
  const { data: donations } = useQuery({ queryKey: ["adminDonations"], queryFn: adminListDonations });
  const { data: pools } = useQuery({ queryKey: ["adminPools"], queryFn: adminListPools });
  const { data: payouts } = useQuery({ queryKey: ["adminPayouts"], queryFn: adminListPayouts });
  const auditLogsParams = {
    limit: 50,
    ...(auditActionFilter !== "all" ? { action: auditActionFilter } : {}),
    ...(auditFromDate ? { from: new Date(auditFromDate).toISOString() } : {}),
    ...(auditToDate ? { to: new Date(new Date(auditToDate).getTime() + 86400000).toISOString() } : {}),
  };
  const { data: auditLogs } = useQuery({ queryKey: ["adminAuditLogs", auditLogsParams], queryFn: () => adminListAuditLogs(auditLogsParams) });
  const { data: versionData } = useQuery({ queryKey: ["appVersion"], queryFn: getVersion, staleTime: Infinity });

  const { data: complianceCount } = useQuery({
    queryKey: ["compliance-alerts-count"],
    queryFn: async () => {
      const r = await fetch("/api/admin/compliance-alerts/count", { credentials: "include" });
      if (!r.ok) return { openCount: 0 };
      return r.json() as Promise<{ openCount: number }>;
    },
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: aiSuggestions } = useQuery<{ title: string; description: string; action: string }[]>({
    queryKey: ["ai-suggestions"],
    queryFn: async () => {
      const res = await fetch("/api/admin/ai/suggestions", { credentials: "include" });
      if (!res.ok) return [];
      const d = (await res.json()) as { suggestions: { title: string; description: string; action: string }[] };
      return d.suggestions ?? [];
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: inboxData, refetch: refetchInbox } = useQuery({
    queryKey: ["adminInbox", inboxPage, inboxUnreadOnly],
    queryFn: () => getAdminInbox(inboxPage, inboxUnreadOnly),
    refetchInterval: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => markInboxMessageRead(id),
    onSuccess: (_data, id) => {
      setSelectedMessage((m) => m?.id === id ? { ...m, isRead: true } : m);
      void refetchInbox();
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllInboxRead,
    onSuccess: () => {
      void refetchInbox();
      toast({ title: "All messages marked as read" });
    },
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => replyToInboxMessage(id, body),
    onSuccess: (_data, vars) => {
      setReplyText("");
      setSelectedMessage((m) => m?.id === vars.id ? { ...m, isRead: true } : m);
      void refetchInbox();
      toast({ title: "Reply sent" });
    },
    onError: () => toast({ title: "Failed to send reply", variant: "destructive" }),
  });

  const { data: boardOfficersData, refetch: refetchBoardOfficers } = useQuery({
    queryKey: ["adminBoardOfficers"],
    queryFn: getAdminBoardOfficers,
    enabled: activeTab === "board-officers",
    staleTime: 30_000,
  });

  const { data: allUsersData, isFetching: allUsersFetching } = useQuery({
    queryKey: ["adminAllUsers", allUsersPage],
    queryFn: () => getAdminAllUsers(allUsersPage),
    enabled: activeTab === "board-officers",
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const activateBoardRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: "secretary" | "treasurer" }) =>
      activateBoardRole(userId, role),
    onSuccess: (result) => {
      void refetchBoardOfficers();
      setBoardSearchQuery("");
      setBoardSearchResults([]);
      setBoardActivatingId(null);
      setBoardKeyModal({ plainKey: result.plainKey, role: result.role, email: result.email });
      toast({ title: `${result.role.charAt(0).toUpperCase() + result.role.slice(1)} activated` });
    },
    onError: () => toast({ title: "Failed to activate board role", variant: "destructive" }),
  });

  const deactivateBoardRoleMutation = useMutation({
    mutationFn: (userId: number) => deactivateBoardRole(userId),
    onSuccess: () => {
      void refetchBoardOfficers();
      toast({ title: "Board role deactivated" });
    },
    onError: () => toast({ title: "Failed to deactivate board role", variant: "destructive" }),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: number) => deleteAdminUser(id),
    onMutate: (id: number) => {
      queryClient.setQueryData(["adminAllUsers", allUsersPage], (old: { users: AdminUserRow[]; page: number; totalPages: number; total: number } | undefined) => {
        if (!old) return old;
        return { ...old, users: old.users.filter((u) => u.id !== id), total: Math.max(0, old.total - 1) };
      });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      void queryClient.invalidateQueries({ queryKey: ["adminBoardOfficers"] });
      setDeleteUserTarget(null);
      setDeleteConfirmInput("");
      setDeleteError(null);
      toast({ title: "User deleted", description: `${result.email} has been permanently removed.` });
    },
    onError: (err: { message?: string; detail?: string }) => {
      void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
      const msg = (err as { detail?: string; message?: string }).detail ?? (err as { message?: string }).message ?? "An error occurred.";
      setDeleteError(msg);
    },
  });

  async function handleBoardSearch(q: string) {
    setBoardSearchQuery(q);
    if (q.length < 3) { setBoardSearchResults([]); return; }
    setBoardSearchLoading(true);
    try {
      const result = await searchAdminUsers(q);
      setBoardSearchResults(result.users);
    } catch {
      setBoardSearchResults([]);
    } finally {
      setBoardSearchLoading(false);
    }
  }

  type TaxFilingRow = {
    id: number;
    year: number;
    filingType: "form_1099c" | "form_990";
    status: "pending" | "filed" | "accepted" | "rejected";
    filedAt: string | null;
    filingCount: number;
    totalAmount: string | null;
  };
  type TaxFilingsSummary = {
    filings: TaxFilingRow[];
    pending1099cByYear: { year: number; count: number }[];
  };

  const { data: taxFilingsSummary, isLoading: taxSummaryLoading } = useQuery<TaxFilingsSummary>({
    queryKey: ["admin-tax-filings-summary"],
    queryFn: async () => {
      const r = await fetch("/api/admin/tax-filings", { credentials: "include" });
      if (!r.ok) return { filings: [], pending1099cByYear: [] };
      return r.json() as Promise<TaxFilingsSummary>;
    },
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const currentYear = new Date().getFullYear();
  const taxYear1099c = taxFilingsSummary?.filings.find(
    (f) => f.year === currentYear && f.filingType === "form_1099c"
  ) ?? null;
  const taxYear990 = taxFilingsSummary?.filings.find(
    (f) => f.year === currentYear && f.filingType === "form_990"
  ) ?? null;
  const pending1099cCount =
    taxFilingsSummary?.pending1099cByYear.find((p) => p.year === currentYear)?.count ?? 0;
  const has1099cWarning =
    pending1099cCount > 0 && (!taxYear1099c || taxYear1099c.status === "pending");

  const createPoolMutation = useMutation({
    mutationFn: (data: CreatePoolBody) => createPool(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["adminPools"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      void queryClient.invalidateQueries({ queryKey: ["adminAuditLogs"] });
      toast({ title: "Pool created", description: "The new pool is now active." });
      setCreatePoolOpen(false);
      setPoolForm(EMPTY_POOL_FORM);
    },
    onError: () => {
      toast({ title: "Failed to create pool", description: "Please check the form and try again.", variant: "destructive" });
    },
  });

  const seedPoolsMutation = useMutation({
    mutationFn: () => adminSeedPools(),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["adminPools"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      toast({ title: result.inserted > 0 ? `Seeded ${result.inserted} pools` : "Already up to date", description: result.message });
    },
    onError: () => {
      toast({ title: "Seed failed", description: "Could not seed pools. Check server logs.", variant: "destructive" });
    },
  });

  const updateDebtMutation = useMutation({
    mutationFn: ({ id, status, reason }: { id: number; status: "queued" | "rejected"; reason?: string }) =>
      adminUpdateDebt(id, { status, reason }),
    onSuccess: (_, { status }) => {
      void queryClient.invalidateQueries({ queryKey: ["adminDebts"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      void queryClient.invalidateQueries({ queryKey: ["adminAuditLogs"] });
      toast({ title: status === "queued" ? "Debt approved" : "Debt rejected", description: status === "queued" ? "Debt moved to the payout queue." : "Debt application rejected." });
    },
    onError: () => {
      toast({ title: "Action failed", description: "Could not update debt status.", variant: "destructive" });
    },
  });

  const verifyOverrideMutation = useMutation({
    mutationFn: ({ id, verificationStatus, reason }: { id: number; verificationStatus: "verified" | "flagged" | "manual_review"; reason?: string }) =>
      fetch(`/api/admin/debts/${id}/verify`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verificationStatus, reason }),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json() as { error: string }).error);
        return r.json();
      }),
    onSuccess: (_data, { verificationStatus }) => {
      void queryClient.invalidateQueries({ queryKey: ["adminDebts"] });
      void queryClient.invalidateQueries({ queryKey: ["adminAuditLogs"] });
      toast({ title: "Verification updated", description: `Debt marked as ${verificationStatus}.` });
      setVerifyOverrideId(null);
      setOverrideReason("");
    },
    onError: (err: Error) => {
      toast({ title: "Override failed", description: err.message, variant: "destructive" });
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => adminApprovePayout(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["adminPayouts"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      void queryClient.invalidateQueries({ queryKey: ["adminAuditLogs"] });
      toast({ title: "Payout approved", description: "The payout has been marked as completed." });
    },
    onError: () => {
      toast({ title: "Failed to approve payout", variant: "destructive" });
    },
  });

  const blockMutation = useMutation({
    mutationFn: (id: number) => adminBlockPayout(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["adminPayouts"] });
      void queryClient.invalidateQueries({ queryKey: ["adminAuditLogs"] });
      toast({ title: "Payout blocked", description: "The payout has been blocked." });
    },
    onError: () => {
      toast({ title: "Failed to block payout", variant: "destructive" });
    },
  });

  const archivePoolMutation = useMutation({
    mutationFn: (id: number) => updatePool(id, { status: "archived" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["adminPools"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      toast({ title: "Pool archived", description: "The pool has been archived and hidden from donors." });
    },
    onError: () => {
      toast({ title: "Failed to archive pool", variant: "destructive" });
    },
  });

  function handleCreatePool(e: React.FormEvent) {
    e.preventDefault();
    const threshold = parseFloat(poolForm.threshold);
    if (!poolForm.name || !poolForm.theme || isNaN(threshold) || threshold <= 0) return;
    createPoolMutation.mutate({
      name: poolForm.name,
      theme: poolForm.theme,
      description: poolForm.description || undefined,
      category: poolForm.category || undefined,
      icon: poolForm.icon || undefined,
      creditorName: poolForm.creditorName || undefined,
      threshold,
    });
  }

  const editPoolMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof updatePool>[1] }) =>
      updatePool(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["adminPools"] });
      void queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      toast({ title: "Pool updated", description: "Pool changes saved successfully." });
      setEditPool(null);
    },
    onError: () => {
      toast({ title: "Failed to update pool", description: "Please check the form and try again.", variant: "destructive" });
    },
  });

  function openEditPool(pool: Pool) {
    setEditPool(pool);
    setEditForm({
      name: pool.name,
      theme: pool.theme ?? "",
      description: pool.description ?? "",
      threshold: String(pool.threshold),
      category: pool.category ?? "",
      icon: pool.icon ?? "",
      sortOrder: String(pool.sortOrder ?? 0),
      status: pool.status,
    });
  }

  function handleEditPool(e: React.FormEvent) {
    e.preventDefault();
    if (!editPool) return;
    const threshold = parseFloat(editForm.threshold);
    if (!editForm.name || !editForm.theme || isNaN(threshold) || threshold <= 0) return;
    editPoolMutation.mutate({
      id: editPool.id,
      data: {
        name: editForm.name,
        theme: editForm.theme,
        description: editForm.description || undefined,
        category: editForm.category || undefined,
        icon: editForm.icon || undefined,
        threshold,
        sortOrder: parseInt(editForm.sortOrder) || 0,
        status: editForm.status as "active" | "funded" | "disbursed" | "archived",
      },
    });
  }

  const filteredPools = (pools ?? [])
    .filter((p) => {
      const catMatch = poolCategoryFilter === "all" || p.category === poolCategoryFilter;
      const statusMatch = poolStatusFilter === "all" || p.status === poolStatusFilter;
      return catMatch && statusMatch;
    })
    .sort((a, b) => {
      if (poolSortBy === "percentFunded") return b.percentFunded - a.percentFunded;
      if (poolSortBy === "createdAt") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (poolSortBy === "threshold") return b.threshold - a.threshold;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });

  const poolTotalPages = Math.max(1, Math.ceil(filteredPools.length / POOLS_PER_PAGE));
  const poolPageClamped = Math.min(poolPage, poolTotalPages);
  const pagedPools = filteredPools.slice((poolPageClamped - 1) * POOLS_PER_PAGE, poolPageClamped * POOLS_PER_PAGE);
  const poolRangeStart = filteredPools.length === 0 ? 0 : (poolPageClamped - 1) * POOLS_PER_PAGE + 1;
  const poolRangeEnd = Math.min(poolPageClamped * POOLS_PER_PAGE, filteredPools.length);

  useEffect(() => {
    if (poolPage > poolTotalPages) setPoolPage(poolTotalPages);
  }, [poolTotalPages, poolPage]);

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> View Site
            </Button>
            <div className="flex items-center gap-2">
              <LogoMark size={28} />
              <span className="font-semibold text-white">Admin Dashboard</span>
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/analytics")}>
              <BarChart3 className="h-4 w-4 mr-1 text-indigo-600" />
              Analytics
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/payout-batches")}>
              <Send className="h-4 w-4 mr-1 text-green-600" />
              Payout Batches
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/payment-dashboard")}>
              <CheckCircle className="h-4 w-4 mr-1 text-blue-600" />
              Banking Status
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/filing-cabinet")}>
              <FolderOpen className="h-4 w-4 mr-1 text-amber-600" />
              Filing Cabinet
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/tax-center")}>
              <FileText className="h-4 w-4 mr-1 text-blue-600" />
              1099-C
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/form-990")}>
              <Receipt className="h-4 w-4 mr-1 text-purple-600" />
              Form 990
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/jobs")}>
              <Activity className="h-4 w-4 mr-1 text-rose-600" />
              Job Queue
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/compliance")}
              className={complianceCount?.openCount ? "border-red-300 text-red-700 hover:bg-red-50" : ""}
            >
              <ShieldAlert className="h-4 w-4 mr-1 text-red-600" />
              Compliance
              {(complianceCount?.openCount ?? 0) > 0 && (
                <Badge className="ml-1.5 px-1.5 py-0 text-[10px] bg-red-600 text-white border-none">
                  {complianceCount!.openCount}
                </Badge>
              )}
            </Button>
            {isSignedIn && <UserButton />}
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {statsLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatTile label="Total Donated" value={`$${stats.totalDonations.toLocaleString()}`} icon={DollarSign} color="bg-primary" />
            <StatTile label="Active Pools" value={stats.activePools} icon={BarChart3} color="bg-emerald-500" />
            <StatTile label="Total Debts" value={stats.totalDebts} icon={FileText} color="bg-blue-500" />
            <StatTile label="Debts Cleared" value={stats.paidDebts} icon={ShieldCheck} color="bg-green-500" />
            <StatTile label="Total Users" value={stats.totalDonors + stats.totalDebtors} icon={Users} color="bg-purple-500" />
            <StatTile
              label="Job Retries (24h)"
              value={stats.jobRequeuedLast24h}
              icon={(stats.jobRequeuedLast24h ?? 0) > 10 ? AlertTriangle : Activity}
              color={(stats.jobRequeuedLast24h ?? 0) > 10 ? "bg-orange-500" : "bg-slate-400"}
            />
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Access restricted to admins. Contact support if you need admin access.
            </CardContent>
          </Card>
        )}

        {/* Ready-to-Pay Banner */}
        {pools && (() => {
          const readyPools = pools.filter(
            (p) => Number(p.balance) >= Number(p.threshold) && ["active", "funded"].includes(p.status)
          );
          if (!readyPools.length) return null;

          const poolSummaries = readyPools.map((p) => {
            const poolDebts = (debts ?? []).filter((d) => d.poolId === p.id && d.status === "queued");
            const eligibleTotal = poolDebts.reduce((sum, d) => sum + Number(d.balance), 0);
            return { pool: p, count: poolDebts.length, total: eligibleTotal };
          });

          return (
            <div className="rounded-xl border border-green-300 bg-green-50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-green-100 shrink-0">
                    <Send className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900">
                      {readyPools.length} pool{readyPools.length > 1 ? "s are" : " is"} ready to pay out
                    </p>
                    <p className="text-sm text-green-700 mt-0.5">
                      Funding threshold reached — create a payout batch to send payments directly to creditors.
                    </p>
                  </div>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 shrink-0" onClick={() => navigate("/admin/payout-batches")}>
                  Create Payout Batch
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {poolSummaries.map(({ pool: p, count, total }) => (
                  <div key={p.id} className="rounded-lg border border-green-200 bg-white/60 px-3 py-2 text-sm">
                    <div className="font-medium text-green-900">{p.name}</div>
                    <div className="text-green-700 text-xs mt-0.5">
                      {count} eligible debt{count !== 1 ? "s" : ""} · ${total.toLocaleString()} to pay
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Tax & IRS Compliance */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold text-base">Tax & IRS Compliance</h2>
            {has1099cWarning && (
              <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-none text-[10px] px-1.5 py-0">
                Action Required
              </Badge>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* 1099-C Tax Center */}
            <Card className={has1099cWarning ? "border-amber-300 bg-amber-50/30" : ""}>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="flex items-center justify-between text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    1099-C Tax Center
                  </div>
                  {taxSummaryLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : taxYear1099c ? (
                    taxYear1099c.status === "filed" || taxYear1099c.status === "accepted" ? (
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Filed {taxYear1099c.status === "accepted" ? "& Accepted" : ""}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                        Pending
                      </Badge>
                    )
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-muted-foreground">
                      Not started
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Tax year: <span className="font-medium text-foreground">{currentYear}</span></div>
                  {(taxYear1099c?.filingCount ?? 0) > 0 && (
                    <div>Records filed: <span className="font-medium text-foreground">{taxYear1099c!.filingCount.toLocaleString()}</span></div>
                  )}
                  {has1099cWarning && (
                    <div className="flex items-start gap-1.5 mt-2 rounded-md bg-amber-100 border border-amber-200 px-2.5 py-2 text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>
                        {pending1099cCount} form{pending1099cCount !== 1 ? "s" : ""} generated but not yet filed with the IRS.
                      </span>
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline" className="w-full text-blue-700 border-blue-200 hover:bg-blue-50" onClick={() => navigate("/admin/tax-center")}>
                  Open 1099-C Center
                </Button>
              </CardContent>
            </Card>

            {/* Form 990-EZ Prep */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="flex items-center justify-between text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-purple-600" />
                    Form 990-EZ Prep
                  </div>
                  {taxSummaryLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : taxYear990 ? (
                    taxYear990.status === "filed" || taxYear990.status === "accepted" ? (
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Filed {taxYear990.status === "accepted" ? "& Accepted" : ""}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                        In Progress
                      </Badge>
                    )
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-muted-foreground">
                      Not started
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Tax year: <span className="font-medium text-foreground">{currentYear}</span></div>
                  {taxYear990?.filedAt && (
                    <div>Filed: <span className="font-medium text-foreground">{new Date(taxYear990.filedAt).toLocaleDateString()}</span></div>
                  )}
                  {!taxYear990 && (
                    <div className="text-xs text-muted-foreground">
                      Annual nonprofit return due by the 15th day of the 5th month after your fiscal year ends.
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline" className="w-full text-purple-700 border-purple-200 hover:bg-purple-50" onClick={() => navigate("/admin/form-990")}>
                  Open Form 990-EZ Prep
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {stats && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="pools">Pools ({pools?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="debts">Debts ({debts?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="debt-profiles">Public profiles</TabsTrigger>
              <TabsTrigger value="donations">Donations ({donations?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="payouts">Payouts ({payouts?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="audit" className="flex items-center gap-1.5">
                <ScrollText className="h-3.5 w-3.5" />
                Audit Log ({auditLogs?.length ?? 0})
              </TabsTrigger>
              <TabsTrigger value="inbox" className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Inbox
                {(inboxData?.unreadCount ?? 0) > 0 && (
                  <span className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {inboxData!.unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="board-officers" className="flex items-center gap-1.5">
                <Key className="h-3.5 w-3.5" />
                Board Officers
                {(boardOfficersData?.officers.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px]">
                    {boardOfficersData!.officers.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Pools tab */}
            <TabsContent value="debt-profiles"><AdminProfileReview /></TabsContent>
            <TabsContent value="pools">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>All Pools ({filteredPools.length}/{pools?.length ?? 0})</span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => seedPoolsMutation.mutate()}
                        disabled={seedPoolsMutation.isPending}
                        title="Seed all 282 categorized relief pools (idempotent — safe to run multiple times)"
                      >
                        {seedPoolsMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                        Seed Pools
                      </Button>
                      <Button size="sm" onClick={() => setCreatePoolOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" /> New Pool
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Filters & Sort */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <select
                      className="text-xs border rounded px-2 py-1 bg-background"
                      value={poolCategoryFilter}
                      onChange={(e) => { setPoolCategoryFilter(e.target.value); setPoolPage(1); }}
                    >
                      <option value="all">All Categories</option>
                      {CATEGORY_OPTIONS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    <select
                      className="text-xs border rounded px-2 py-1 bg-background"
                      value={poolStatusFilter}
                      onChange={(e) => { setPoolStatusFilter(e.target.value); setPoolPage(1); }}
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="funded">Funded</option>
                      <option value="disbursed">Disbursed</option>
                      <option value="archived">Archived</option>
                    </select>
                    <select
                      className="text-xs border rounded px-2 py-1 bg-background"
                      value={poolSortBy}
                      onChange={(e) => { setPoolSortBy(e.target.value as typeof poolSortBy); setPoolPage(1); }}
                    >
                      <option value="sortOrder">Sort: Default</option>
                      <option value="percentFunded">Sort: Fill Rate ↓</option>
                      <option value="threshold">Sort: Goal ↓</option>
                      <option value="createdAt">Sort: Newest First</option>
                    </select>
                  </div>

                  {filteredPools.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">No pools match the current filters.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 pr-3 w-8"></th>
                            <th className="pb-2 pr-4">Pool</th>
                            <th className="pb-2 pr-4">Category</th>
                            <th className="pb-2 pr-4">Balance / Goal</th>
                            <th className="pb-2 pr-4 min-w-[100px]">Funded</th>
                            <th className="pb-2 pr-4">Status</th>
                            <th className="pb-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedPools.map((pool) => (
                            <tr key={pool.id} className="border-b last:border-0">
                              <td className="py-3 pr-3 text-xl">{pool.icon ?? "🏦"}</td>
                              <td className="py-3 pr-4">
                                <div className="font-medium">{pool.name}</div>
                                {pool.description && (
                                  <div className="text-xs text-muted-foreground mt-0.5 max-w-[260px] truncate">{pool.description}</div>
                                )}
                              </td>
                              <td className="py-3 pr-4">
                                {pool.category ? (
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[pool.category] ?? "bg-muted text-muted-foreground"}`}>
                                    {CATEGORY_OPTIONS.find((c) => c.value === pool.category)?.label ?? pool.category}
                                  </span>
                                ) : <span className="text-muted-foreground text-xs">—</span>}
                              </td>
                              <td className="py-3 pr-4">
                                <div className="text-primary font-medium">${Number(pool.balance).toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">of ${Number(pool.threshold).toLocaleString()}</div>
                              </td>
                              <td className="py-3 pr-4">
                                <div className="text-xs text-muted-foreground mb-1">{pool.percentFunded}%</div>
                                <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full transition-all"
                                    style={{ width: `${pool.percentFunded}%` }}
                                  />
                                </div>
                              </td>
                              <td className="py-3 pr-4">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                                  pool.status === "active" ? "bg-green-100 text-green-700" :
                                  pool.status === "funded" ? "bg-blue-100 text-blue-700" :
                                  pool.status === "archived" ? "bg-gray-100 text-gray-500" :
                                  "bg-muted text-muted-foreground"
                                }`}>
                                  {pool.status}
                                </span>
                              </td>
                              <td className="py-3">
                                <div className="flex items-center gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7 px-2"
                                    onClick={() => openEditPool(pool as Pool)}
                                  >
                                    Edit
                                  </Button>
                                  {pool.status !== "archived" ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs h-7 px-2 text-muted-foreground hover:text-destructive hover:border-destructive"
                                      disabled={archivePoolMutation.isPending}
                                      onClick={() => archivePoolMutation.mutate(pool.id)}
                                    >
                                      Archive
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground italic">archived</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {filteredPools.length > 0 && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t text-sm text-muted-foreground">
                      <span>
                        Showing {poolRangeStart}–{poolRangeEnd} of {filteredPools.length} pools
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          disabled={poolPageClamped <= 1}
                          onClick={() => setPoolPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                        <span className="px-2 text-xs">
                          Page {poolPageClamped} of {poolTotalPages}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          disabled={poolPageClamped >= poolTotalPages}
                          onClick={() => setPoolPage((p) => Math.min(poolTotalPages, p + 1))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Debts tab */}
            <TabsContent value="debts">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>All Debt Submissions</span>
                    <div className="flex items-center gap-3 text-xs font-normal text-muted-foreground">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200"><Flag className="h-3 w-3" />Flagged</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"><AlertTriangle className="h-3 w-3" />Manual Review</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200"><ShieldCheck className="h-3 w-3" />Verified</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!debts || debts.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">No debts submitted yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {debts.map((debt) => {
                        const d = debt as typeof debt & {
                          verificationStatus?: string | null;
                          verificationScore?: number | null;
                          verificationNotes?: string | null;
                          verificationOverrideReason?: string | null;
                          debtType?: string | null;
                          extractedData?: {
                            creditor?: string | null;
                            accountNumber?: string | null;
                            holderName?: string | null;
                            balance?: number | null;
                            date?: string | null;
                            readable?: boolean;
                          } | null;
                          accountNumber?: string | null;
                          accountHolderName?: string | null;
                        };
                        const vs = d.verificationStatus;
                        const score = d.verificationScore;
                        const notes = d.verificationNotes;
                        const isFlagged = vs === "flagged";
                        const isManual = vs === "manual_review";
                        const isVerified = vs === "verified";
                        const isExpanded = expandedDebtId === d.id;
                        const rowBg = isFlagged ? "border-red-200 bg-red-50/40" : isManual ? "border-amber-200 bg-amber-50/30" : "border-border";

                        return (
                          <div key={d.id} className={`rounded-lg border ${rowBg} overflow-hidden`}>
                            {/* Row header — click to expand */}
                            <div
                              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-black/[0.03] transition-colors"
                              onClick={() => setExpandedDebtId(isExpanded ? null : d.id)}
                            >
                              <span className="text-muted-foreground text-xs w-8 shrink-0">#{d.id}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {isFlagged && <Flag className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                                  <span className="font-medium text-sm truncate">{d.creditor}</span>
                                  {d.debtType && <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{d.debtType}</span>}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  ${Number(d.balance).toLocaleString()} · {d.poolName ?? "no pool"} · <span className="capitalize">{d.status}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {vs ? (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                    isFlagged ? "bg-red-100 text-red-700 border border-red-200"
                                    : isManual ? "bg-amber-100 text-amber-700 border border-amber-200"
                                    : isVerified ? "bg-green-100 text-green-700 border border-green-200"
                                    : "bg-gray-100 text-gray-600 border"
                                  }`}>
                                    {isFlagged ? <Flag className="h-3 w-3" /> : isManual ? <AlertTriangle className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                                    {isFlagged ? "Flagged" : isManual ? "Review" : isVerified ? "Verified" : vs}
                                    {score !== null && score !== undefined && ` · ${score}`}
                                  </span>
                                ) : null}
                                <span className="text-muted-foreground text-xs">{isExpanded ? "▲" : "▼"}</span>
                              </div>
                            </div>

                            {/* Expanded detail panel */}
                            {isExpanded && (
                              <div className="border-t px-4 py-4 space-y-4 bg-background/60">
                                {/* Side-by-side comparison */}
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Document Verification</p>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="pb-1.5 pr-4 text-left font-medium text-muted-foreground">Field</th>
                                          <th className="pb-1.5 pr-4 text-left font-medium">Debtor Entered</th>
                                          <th className="pb-1.5 pr-4 text-left font-medium">Document Shows</th>
                                          <th className="pb-1.5 text-left font-medium">Match</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-border/50">
                                        {[
                                          { field: "Creditor", submitted: d.creditor, extracted: d.extractedData?.creditor },
                                          { field: "Account #", submitted: d.accountNumber ?? "—", extracted: d.extractedData?.accountNumber },
                                          { field: "Holder Name", submitted: d.accountHolderName ?? "—", extracted: d.extractedData?.holderName },
                                          { field: "Balance", submitted: `$${Number(d.balance).toLocaleString()}`, extracted: d.extractedData?.balance != null ? `$${d.extractedData.balance.toLocaleString()}` : null },
                                          { field: "Statement Date", submitted: "—", extracted: d.extractedData?.date },
                                        ].map(({ field, submitted, extracted }) => {
                                          const hasData = extracted != null;
                                          const matches = hasData && String(submitted ?? "").toLowerCase() !== "—" &&
                                            String(extracted).toLowerCase().includes(String(submitted ?? "").toLowerCase().replace(/[$,]/g, ""));
                                          return (
                                            <tr key={field}>
                                              <td className="py-1.5 pr-4 text-muted-foreground font-medium">{field}</td>
                                              <td className="py-1.5 pr-4">{submitted ?? "—"}</td>
                                              <td className="py-1.5 pr-4">{hasData ? String(extracted) : <span className="text-muted-foreground italic">not found</span>}</td>
                                              <td className="py-1.5">
                                                {!hasData ? (
                                                  <span className="text-muted-foreground">—</span>
                                                ) : matches ? (
                                                  <span className="text-green-600 font-medium">✓</span>
                                                ) : (
                                                  <span className="text-red-500 font-medium">✗</span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  {notes && (
                                    <p className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded p-2">{notes}</p>
                                  )}
                                  {d.verificationOverrideReason && (
                                    <p className="mt-2 text-xs text-blue-700 bg-blue-50 rounded p-2">Admin note: {d.verificationOverrideReason}</p>
                                  )}
                                </div>

                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-2 pt-1">
                                  {d.status === "pending" && (
                                    <>
                                      <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50 h-7 px-2"
                                        disabled={updateDebtMutation.isPending}
                                        onClick={(e) => { e.stopPropagation(); updateDebtMutation.mutate({ id: d.id, status: "queued" }); }}>
                                        {updateDebtMutation.isPending && updateDebtMutation.variables?.id === d.id && updateDebtMutation.variables.status === "queued"
                                          ? <Loader2 className="h-3 w-3 animate-spin" />
                                          : <><CheckCircle className="h-3.5 w-3.5 mr-1" />Approve to Queue</>}
                                      </Button>
                                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-2"
                                        disabled={updateDebtMutation.isPending}
                                        onClick={(e) => { e.stopPropagation(); setRejectDebtId(d.id); setRejectReason(""); }}>
                                        <><XCircle className="h-3.5 w-3.5 mr-1" />Reject</>
                                      </Button>
                                    </>
                                  )}
                                  {vs !== "verified" && (
                                    <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50 h-7 px-2"
                                      disabled={verifyOverrideMutation.isPending}
                                      onClick={(e) => { e.stopPropagation(); verifyOverrideMutation.mutate({ id: d.id, verificationStatus: "verified", reason: undefined }); }}>
                                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />Mark Verified
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 px-2"
                                    onClick={(e) => { e.stopPropagation(); setVerifyOverrideId(d.id); setOverrideReason(""); }}>
                                    <AlertTriangle className="h-3.5 w-3.5 mr-1" />Override with Reason
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Donations tab */}
            <TabsContent value="donations">
              <Card>
                <CardHeader><CardTitle>All Donations</CardTitle></CardHeader>
                <CardContent>
                  {!donations || donations.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">No donations yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 pr-4">ID</th>
                            <th className="pb-2 pr-4">Pool</th>
                            <th className="pb-2 pr-4">Amount</th>
                            <th className="pb-2 pr-4">Type</th>
                            <th className="pb-2 pr-4">Status</th>
                            <th className="pb-2">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {donations.map((d) => (
                            <tr key={d.id} className="border-b last:border-0">
                              <td className="py-2 pr-4 text-muted-foreground">#{d.id}</td>
                              <td className="py-2 pr-4">{d.poolName || "-"}</td>
                              <td className="py-2 pr-4 text-primary font-medium">${Number(d.amount).toLocaleString()}</td>
                              <td className="py-2 pr-4">{d.isRecurring ? "Monthly" : "One-time"}</td>
                              <td className="py-2 pr-4 capitalize">{d.status}</td>
                              <td className="py-2 text-muted-foreground">{new Date(d.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payouts tab */}
            <TabsContent value="payouts">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Pool Payouts</span>
                    {payouts && payouts.filter((p) => p.status === "pending").length > 0 && (
                      <span className="text-sm font-normal text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
                        {payouts.filter((p) => p.status === "pending").length} pending approval
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!payouts || payouts.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">No payouts yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 pr-4">Pool</th>
                            <th className="pb-2 pr-4">Amount</th>
                            <th className="pb-2 pr-4">Status</th>
                            <th className="pb-2 pr-4">Executed</th>
                            <th className="pb-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.map((p) => (
                            <tr key={p.id} className="border-b last:border-0">
                              <td className="py-2 pr-4 font-medium">{p.poolName || "-"}</td>
                              <td className="py-2 pr-4 text-primary font-medium">${Number(p.amount).toLocaleString()}</td>
                              <td className="py-2 pr-4">
                                <span className={`capitalize px-2 py-0.5 rounded-full text-xs font-medium ${
                                  p.status === "pending" ? "bg-amber-100 text-amber-700" :
                                  p.status === "completed" ? "bg-green-100 text-green-700" :
                                  "bg-red-100 text-red-700"
                                }`}>
                                  {p.status}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-muted-foreground">
                                {p.executedAt ? new Date(p.executedAt).toLocaleDateString() : "-"}
                              </td>
                              <td className="py-2">
                                {p.status === "pending" ? (
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      className="h-7 px-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                                      disabled={approveMutation.isPending || blockMutation.isPending}
                                      onClick={() => setPayoutConfirmId(p.id)}
                                    >
                                      <Zap className="h-3.5 w-3.5 mr-1" />Trigger Payout
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-2"
                                      disabled={approveMutation.isPending || blockMutation.isPending}
                                      onClick={() => blockMutation.mutate(p.id)}
                                    >
                                      {blockMutation.isPending && blockMutation.variables === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><XCircle className="h-3.5 w-3.5 mr-1" />Block</>}
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Log tab */}
            <TabsContent value="audit">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ScrollText className="h-5 w-5 text-primary" />
                    Audit Log
                    <span className="text-xs font-normal text-muted-foreground ml-auto">Last 50 entries · Read-only</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Filter by action:</label>
                    <select
                      className="border rounded-md px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      value={auditActionFilter}
                      onChange={(e) => setAuditActionFilter(e.target.value)}
                    >
                      <option value="all">All actions</option>
                      <option value="document_downloaded">Certificate Downloaded</option>
                      <option value="job_requeued">Job Re-queued</option>
                      <option value="payout_approved">Payout Approved</option>
                      <option value="payout_blocked">Payout Blocked</option>
                      <option value="pool_created">Pool Created</option>
                      <option value="debt_status_changed">Debt Status Changed</option>
                      <option value="user_role_changed">User Role Changed</option>
                    </select>
                    <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">From:</span>
                    <input
                      type="date"
                      className="border rounded-md px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      value={auditFromDate}
                      onChange={(e) => setAuditFromDate(e.target.value)}
                    />
                    <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">To:</span>
                    <input
                      type="date"
                      className="border rounded-md px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      value={auditToDate}
                      onChange={(e) => setAuditToDate(e.target.value)}
                    />
                    {(auditFromDate || auditToDate) && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                        onClick={() => { setAuditFromDate(""); setAuditToDate(""); }}
                      >
                        Clear dates
                      </button>
                    )}
                  </div>
                  {!auditLogs || auditLogs.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">
                      {(auditActionFilter !== "all" || auditFromDate || auditToDate) ? "No entries match the selected filters." : "No audit entries yet. Admin actions will appear here."}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 pr-4">Timestamp</th>
                            <th className="pb-2 pr-4">Actor</th>
                            <th className="pb-2 pr-4">Action</th>
                            <th className="pb-2 pr-4">Entity</th>
                            <th className="pb-2">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.map((entry) => {
                            const meta = entry.metadata as Record<string, unknown> | null | undefined;
                            let detailText: string;
                            if (entry.action === "job_requeued" && meta) {
                              const parts: string[] = [];
                              if (meta.jobType) parts.push(`job: ${meta.jobType}`);
                              if (meta.previousAttempts !== undefined) parts.push(`attempts: ${meta.previousAttempts}`);
                              Object.entries(meta).forEach(([k, v]) => {
                                if (k !== "jobType" && k !== "previousAttempts") parts.push(`${k}: ${v}`);
                              });
                              detailText = parts.join(", ");
                            } else {
                              detailText = meta ? Object.entries(meta).map(([k, v]) => `${k}: ${v}`).join(", ") : "—";
                            }
                            return (
                              <tr key={entry.id} className="border-b last:border-0">
                                <td className="py-2 pr-4 text-muted-foreground text-xs whitespace-nowrap">
                                  {new Date(entry.createdAt).toLocaleString()}
                                </td>
                                <td className="py-2 pr-4 max-w-[180px] truncate" title={entry.actorEmail}>
                                  {entry.actorEmail === "cron@debtbridge.org" ? (
                                    <span className="text-muted-foreground italic">System (cron)</span>
                                  ) : (
                                    entry.actorEmail
                                  )}
                                </td>
                                <td className="py-2 pr-4">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                    entry.action === "payout_approved" ? "bg-green-100 text-green-700" :
                                    entry.action === "payout_blocked" ? "bg-red-100 text-red-700" :
                                    entry.action === "pool_created" ? "bg-blue-100 text-blue-700" :
                                    entry.action === "debt_status_changed" ? "bg-amber-100 text-amber-700" :
                                    entry.action === "user_role_changed" ? "bg-purple-100 text-purple-700" :
                                    entry.action === "job_requeued" ? "bg-orange-100 text-orange-700" :
                                    "bg-muted text-muted-foreground"
                                  }`}>
                                    {entry.action === "job_requeued" ? "Job Re-queued" : entry.action.replace(/_/g, " ")}
                                  </span>
                                </td>
                                <td className="py-2 pr-4 text-muted-foreground capitalize">
                                  {entry.entityType}{entry.entityId ? ` #${entry.entityId}` : ""}
                                </td>
                                <td className="py-2 text-xs text-muted-foreground max-w-[240px] truncate" title={meta ? JSON.stringify(meta) : ""}>
                                  {detailText || "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Inbox tab */}
            <TabsContent value="inbox">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                    <span className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Support Inbox
                      {(inboxData?.unreadCount ?? 0) > 0 && (
                        <Badge variant="destructive" className="text-xs">{inboxData!.unreadCount} unread</Badge>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-primary"
                          checked={inboxUnreadOnly}
                          onChange={(e) => { setInboxUnreadOnly(e.target.checked); setInboxPage(1); setSelectedMessage(null); }}
                        />
                        Unread only
                      </label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAllReadMutation.mutate()}
                        disabled={markAllReadMutation.isPending || (inboxData?.unreadCount ?? 0) === 0}
                      >
                        {markAllReadMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <MailOpen className="h-3.5 w-3.5 mr-1" />}
                        Mark all read
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!inboxData || inboxData.messages.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <Mail className="h-8 w-8 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">{inboxUnreadOnly ? "No unread messages." : "No messages yet. Emails sent to support@debtbridge.org will appear here."}</p>
                    </div>
                  ) : (
                    <div className="flex gap-4 h-[600px]">
                      {/* Message list */}
                      <div className="w-80 flex-shrink-0 border rounded-lg overflow-y-auto">
                        {inboxData.messages.map((msg) => (
                          <button
                            key={msg.id}
                            type="button"
                            onClick={() => {
                              setSelectedMessage(msg);
                              setReplyText("");
                              if (!msg.isRead) markReadMutation.mutate(msg.id);
                            }}
                            className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${selectedMessage?.id === msg.id ? "bg-muted" : ""}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  {!msg.isRead && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary" />}
                                  <span className={`text-xs truncate ${!msg.isRead ? "font-semibold" : "text-muted-foreground"}`}>
                                    {msg.fromEmail.includes("<") ? msg.fromEmail.match(/^([^<]+)/)?.[1]?.trim() ?? msg.fromEmail : msg.fromEmail}
                                  </span>
                                </div>
                                <p className={`text-sm mt-0.5 truncate ${!msg.isRead ? "font-medium" : ""}`}>{msg.subject}</p>
                                <p className="text-xs text-muted-foreground truncate mt-0.5">
                                  {(msg.textBody ?? "").slice(0, 80) || "(no text body)"}
                                </p>
                              </div>
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0 mt-0.5">
                                {new Date(msg.receivedAt).toLocaleDateString()}
                              </span>
                            </div>
                          </button>
                        ))}
                        {/* Pagination */}
                        {(inboxData.pages ?? 1) > 1 && (
                          <div className="flex items-center justify-center gap-2 p-3 border-t">
                            <Button size="sm" variant="ghost" disabled={inboxPage <= 1} onClick={() => setInboxPage((p) => p - 1)}>←</Button>
                            <span className="text-xs text-muted-foreground">{inboxPage} / {inboxData.pages}</span>
                            <Button size="sm" variant="ghost" disabled={inboxPage >= inboxData.pages} onClick={() => setInboxPage((p) => p + 1)}>→</Button>
                          </div>
                        )}
                      </div>

                      {/* Message detail */}
                      {selectedMessage ? (
                        <div className="flex-1 border rounded-lg flex flex-col overflow-hidden">
                          {/* Header */}
                          <div className="px-5 py-4 border-b bg-muted/30">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-semibold text-base leading-tight">{selectedMessage.subject}</h3>
                              {!selectedMessage.isRead && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-shrink-0 text-xs"
                                  disabled={markReadMutation.isPending}
                                  onClick={() => markReadMutation.mutate(selectedMessage.id)}
                                >
                                  {markReadMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <MailOpen className="h-3 w-3 mr-1" />}
                                  Mark as read
                                </Button>
                              )}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              <span><strong>From:</strong> {selectedMessage.fromEmail}</span>
                              <span><strong>To:</strong> {selectedMessage.toEmail}</span>
                              <span><strong>Received:</strong> {new Date(selectedMessage.receivedAt).toLocaleString()}</span>
                              {selectedMessage.matchedUserName && (
                                <button
                                  type="button"
                                  className="text-primary font-medium hover:underline"
                                  onClick={() => setActiveTab("debts")}
                                  title="Switch to Debts tab to find this user's applications"
                                >
                                  Matched: {selectedMessage.matchedUserName} ({selectedMessage.matchedUserEmail}) →
                                </button>
                              )}
                              {!selectedMessage.matchedUserId && (
                                <span className="text-muted-foreground italic">No matching account found</span>
                              )}
                            </div>
                          </div>

                          {/* Body */}
                          <div className="flex-1 overflow-y-auto px-5 py-4">
                            {selectedMessage.htmlBody ? (
                              <iframe
                                srcDoc={selectedMessage.htmlBody}
                                className="w-full min-h-48 border-0"
                                title="Email body"
                                sandbox="allow-same-origin"
                                style={{ height: "240px" }}
                              />
                            ) : (
                              <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed text-foreground">
                                {selectedMessage.textBody ?? "(empty message)"}
                              </pre>
                            )}
                          </div>

                          {/* Reply form */}
                          <div className="border-t px-5 py-4 bg-muted/10">
                            <Label htmlFor="reply-body" className="text-xs font-medium flex items-center gap-1.5 mb-2">
                              <Reply className="h-3.5 w-3.5" />
                              Reply to {selectedMessage.fromEmail.includes("<")
                                ? selectedMessage.fromEmail.match(/<([^>]+)>/)?.[1]
                                : selectedMessage.fromEmail}
                            </Label>
                            <Textarea
                              id="reply-body"
                              placeholder="Type your reply here..."
                              className="min-h-[100px] text-sm"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                            />
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <span className="text-[11px] text-muted-foreground">Sends from noreply@debtbridge.org · reply-to support@debtbridge.org</span>
                              <Button
                                size="sm"
                                disabled={!replyText.trim() || replyMutation.isPending}
                                onClick={() => replyMutation.mutate({ id: selectedMessage.id, body: replyText })}
                              >
                                {replyMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                                Send Reply
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 border rounded-lg flex items-center justify-center text-muted-foreground">
                          <div className="text-center">
                            <MailOpen className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">Select a message to read</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Board Officers tab */}
            <TabsContent value="board-officers">
              <div className="space-y-6">
                {/* Active Officers */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="h-4 w-4 text-[#A8B5A2]" />
                      Active Board Officers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!boardOfficersData || boardOfficersData.officers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No board officers are currently active.</p>
                    ) : (
                      <div className="space-y-2">
                        {boardOfficersData.officers.map((officer: BoardOfficer) => (
                          <div key={officer.id} className="flex items-center justify-between rounded-lg border px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#A8B5A2]/20">
                                <Shield className="h-4 w-4 text-[#A8B5A2]" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{officer.displayName ?? officer.email}</p>
                                <p className="text-xs text-muted-foreground">{officer.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge
                                variant="secondary"
                                className={officer.boardRole === "secretary"
                                  ? "bg-blue-50 text-blue-700 border-blue-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"}
                              >
                                {officer.boardRole === "secretary" ? "Secretary" : "Treasurer"}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={deactivateBoardRoleMutation.isPending}
                                onClick={() => deactivateBoardRoleMutation.mutate(officer.id)}
                              >
                                <UserMinus className="h-3.5 w-3.5 mr-1" />
                                Revoke
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* All Users — Assign Officer */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-[#A8B5A2]" />
                      Assign Board Officer
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Input
                        placeholder="Filter by email or name…"
                        value={boardSearchQuery}
                        onChange={(e) => void handleBoardSearch(e.target.value)}
                      />
                    </div>

                    {/* Search results take priority, otherwise show paginated all-users */}
                    {boardSearchQuery.length >= 3 ? (
                      <>
                        {boardSearchLoading && <p className="text-xs text-muted-foreground py-2">Searching…</p>}
                        {!boardSearchLoading && boardSearchResults.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No users found matching "{boardSearchQuery}".</p>
                        )}
                        {boardSearchResults.length > 0 && (
                          <div className="rounded-md border overflow-hidden divide-y">
                            {boardSearchResults.map((u) => (
                              <UserActivateRow
                                key={u.id}
                                user={u}
                                activatingId={boardActivatingId}
                                activatingRole={boardActivatingRole}
                                isPending={activateBoardRoleMutation.isPending}
                                onActivate={(id, role) => {
                                  setBoardActivatingId(id);
                                  setBoardActivatingRole(role);
                                  activateBoardRoleMutation.mutate({ userId: id, role });
                                }}
                                onDeactivate={(id) => deactivateBoardRoleMutation.mutate(id)}
                                deactivatePending={deactivateBoardRoleMutation.isPending}
                                onDelete={(u) => { setDeleteUserTarget(u); setDeleteConfirmInput(""); setDeleteError(null); }}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {allUsersFetching && !allUsersData && (
                          <div className="flex justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        {allUsersData && (
                          <>
                            <div className="rounded-md border overflow-hidden divide-y">
                              {allUsersData.users.map((u: AdminUserRow) => (
                                <UserActivateRow
                                  key={u.id}
                                  user={u}
                                  activatingId={boardActivatingId}
                                  activatingRole={boardActivatingRole}
                                  isPending={activateBoardRoleMutation.isPending}
                                  onActivate={(id, role) => {
                                    setBoardActivatingId(id);
                                    setBoardActivatingRole(role);
                                    activateBoardRoleMutation.mutate({ userId: id, role });
                                  }}
                                  onDeactivate={(id) => deactivateBoardRoleMutation.mutate(id)}
                                  deactivatePending={deactivateBoardRoleMutation.isPending}
                                  onDelete={(u) => { setDeleteUserTarget(u); setDeleteConfirmInput(""); setDeleteError(null); }}
                                />
                              ))}
                            </div>
                            {allUsersData.totalPages > 1 && (
                              <div className="flex items-center justify-between pt-1">
                                <p className="text-xs text-muted-foreground">
                                  Page {allUsersData.page} of {allUsersData.totalPages} ({allUsersData.total} users)
                                </p>
                                <div className="flex gap-1.5">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={allUsersPage <= 1 || allUsersFetching}
                                    onClick={() => setAllUsersPage((p) => p - 1)}
                                  >
                                    Previous
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={allUsersPage >= allUsersData.totalPages || allUsersFetching}
                                    onClick={() => setAllUsersPage((p) => p + 1)}
                                  >
                                    Next
                                  </Button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Instructions */}
                <Card className="bg-muted/40">
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <strong>How it works:</strong> When you assign a board officer, a one-time gateway key is generated and shown to you. Share this key with the officer — they use it at <code className="bg-muted rounded px-1">/board-entry</code> to access their dashboard. Revoking a role immediately invalidates their session. Keys cannot be retrieved after initial display.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Board Key Modal */}
      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserTarget !== null} onOpenChange={(open) => { if (!open) { setDeleteUserTarget(null); setDeleteConfirmInput(""); setDeleteError(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          {deleteUserTarget && (
            <div className="space-y-4">
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm space-y-1">
                <p className="font-medium text-destructive">This action is permanent and cannot be undone.</p>
                <p className="text-muted-foreground">All of this user's debts, donations, and associated records will be permanently erased from the system.</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-sm text-muted-foreground">
                  Account: <span className="font-medium text-foreground">{deleteUserTarget.displayName ?? deleteUserTarget.email}</span>
                </p>
                {deleteUserTarget.displayName && (
                  <p className="text-xs text-muted-foreground">{deleteUserTarget.email}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="delete-confirm-input" className="text-sm">
                  Type{deleteUserTarget.displayName ? <> <span className="font-medium">{deleteUserTarget.displayName}</span> or their email</> : <> <span className="font-medium">{deleteUserTarget.email}</span></>} to confirm
                </Label>
                <Input
                  id="delete-confirm-input"
                  value={deleteConfirmInput}
                  onChange={(e) => { setDeleteConfirmInput(e.target.value); setDeleteError(null); }}
                  placeholder={deleteUserTarget.displayName ?? deleteUserTarget.email}
                  autoComplete="off"
                  disabled={deleteUserMutation.isPending}
                />
              </div>
              {deleteError && (
                <p className="text-sm text-destructive">{deleteError}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteUserTarget(null); setDeleteConfirmInput(""); setDeleteError(null); }} disabled={deleteUserMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!deleteUserTarget || (deleteConfirmInput.trim() !== (deleteUserTarget.displayName ?? deleteUserTarget.email) && deleteConfirmInput.trim() !== deleteUserTarget.email) || deleteUserMutation.isPending}
              onClick={() => { if (deleteUserTarget) { setDeleteError(null); deleteUserMutation.mutate(deleteUserTarget.id); } }}
            >
              {deleteUserMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={boardKeyModal !== null} onOpenChange={(open) => { if (!open) { setBoardKeyModal(null); setBoardKeyCopied(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-4 w-4 text-[#A8B5A2]" />
              {boardKeyModal ? `${boardKeyModal.role.charAt(0).toUpperCase() + boardKeyModal.role.slice(1)} Gateway Key` : "Gateway Key"}
            </DialogTitle>
          </DialogHeader>
          {boardKeyModal && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Share this key with <strong>{boardKeyModal.email}</strong>. They will use it at{" "}
                <code className="bg-muted rounded px-1 text-xs">/board-entry</code> to access their dashboard.
              </p>
              <div className="rounded-lg bg-muted p-3 flex items-center justify-between gap-2">
                <code className="text-sm font-mono break-all select-all">{boardKeyModal.plainKey}</code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => {
                    void navigator.clipboard.writeText(boardKeyModal.plainKey);
                    setBoardKeyCopied(true);
                    setTimeout(() => setBoardKeyCopied(false), 2000);
                  }}
                >
                  {boardKeyCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-destructive font-medium">
                ⚠ This key will not be shown again. Copy it now before closing.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setBoardKeyModal(null); setBoardKeyCopied(false); }}>
              Done — I've copied the key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout Confirmation Dialog */}
      <Dialog open={payoutConfirmId !== null} onOpenChange={(open) => { if (!open) setPayoutConfirmId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Trigger Payout</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will mark the payout as completed and release funds to the recipient. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutConfirmId(null)}>
              Cancel
            </Button>
            <Button
              disabled={approveMutation.isPending}
              onClick={() => {
                if (payoutConfirmId !== null) {
                  approveMutation.mutate(payoutConfirmId, {
                    onSettled: () => setPayoutConfirmId(null),
                  });
                }
              }}
            >
              {approveMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing…</> : "Confirm Payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Pool Dialog */}
      <Dialog open={createPoolOpen} onOpenChange={setCreatePoolOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Pool</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePool} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pool-name">Pool Name <span className="text-destructive">*</span></Label>
              <Input
                id="pool-name"
                placeholder="e.g. Emergency Room Bills"
                value={poolForm.name}
                onChange={(e) => setPoolForm((f) => ({ ...f, name: e.target.value }))}
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pool-creditor">Creditor Name</Label>
              <Input
                id="pool-creditor"
                placeholder="e.g. Acme Hospital System"
                value={poolForm.creditorName}
                onChange={(e) => setPoolForm((f) => ({ ...f, creditorName: e.target.value }))}
                maxLength={150}
              />
              <p className="text-xs text-muted-foreground">When set, debt submissions from this creditor auto-route here.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pool-category">Category</Label>
                <select
                  id="pool-category"
                  className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                  value={poolForm.category}
                  onChange={(e) => setPoolForm((f) => ({ ...f, category: e.target.value }))}
                >
                  <option value="">No category</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pool-icon">Icon (emoji)</Label>
                <Input
                  id="pool-icon"
                  placeholder="e.g. 🏥"
                  value={poolForm.icon}
                  onChange={(e) => setPoolForm((f) => ({ ...f, icon: e.target.value }))}
                  maxLength={10}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pool-theme">Theme <span className="text-destructive">*</span></Label>
              <Input
                id="pool-theme"
                placeholder="e.g. medical"
                value={poolForm.theme}
                onChange={(e) => setPoolForm((f) => ({ ...f, theme: e.target.value }))}
                required
                maxLength={50}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pool-description">Description</Label>
              <Textarea
                id="pool-description"
                placeholder="Describe who this pool helps and why it matters…"
                value={poolForm.description}
                onChange={(e) => setPoolForm((f) => ({ ...f, description: e.target.value }))}
                maxLength={500}
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pool-threshold">Funding Goal ($) <span className="text-destructive">*</span></Label>
              <Input
                id="pool-threshold"
                type="number"
                placeholder="50000"
                min={1}
                step={1}
                value={poolForm.threshold}
                onChange={(e) => setPoolForm((f) => ({ ...f, threshold: e.target.value }))}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreatePoolOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createPoolMutation.isPending || !poolForm.name || !poolForm.theme || !poolForm.threshold}
              >
                {createPoolMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : "Create Pool"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Pool Dialog */}
      <Dialog open={editPool !== null} onOpenChange={(open) => { if (!open) setEditPool(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Pool</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPool} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-pool-name">Pool Name <span className="text-destructive">*</span></Label>
              <Input
                id="edit-pool-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                required
                maxLength={100}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-pool-category">Category</Label>
                <select
                  id="edit-pool-category"
                  className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                  value={editForm.category}
                  onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                >
                  <option value="">No category</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-pool-icon">Icon (emoji)</Label>
                <Input
                  id="edit-pool-icon"
                  placeholder="e.g. 🏥"
                  value={editForm.icon}
                  onChange={(e) => setEditForm((f) => ({ ...f, icon: e.target.value }))}
                  maxLength={10}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-pool-theme">Theme <span className="text-destructive">*</span></Label>
              <Input
                id="edit-pool-theme"
                value={editForm.theme}
                onChange={(e) => setEditForm((f) => ({ ...f, theme: e.target.value }))}
                required
                maxLength={50}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-pool-description">Description</Label>
              <Textarea
                id="edit-pool-description"
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                maxLength={500}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-pool-threshold">Funding Goal ($) <span className="text-destructive">*</span></Label>
                <Input
                  id="edit-pool-threshold"
                  type="number"
                  min={1}
                  step={1}
                  value={editForm.threshold}
                  onChange={(e) => setEditForm((f) => ({ ...f, threshold: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-pool-sort">Sort Order</Label>
                <Input
                  id="edit-pool-sort"
                  type="number"
                  min={0}
                  step={1}
                  value={editForm.sortOrder}
                  onChange={(e) => setEditForm((f) => ({ ...f, sortOrder: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-pool-status">Status</Label>
              <select
                id="edit-pool-status"
                className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                value={editForm.status}
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="funded">Funded</option>
                <option value="disbursed">Disbursed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditPool(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={editPoolMutation.isPending || !editForm.name || !editForm.theme || !editForm.threshold}
              >
                {editPoolMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Verification override dialog */}
      <Dialog open={verifyOverrideId !== null} onOpenChange={(open) => { if (!open) { setVerifyOverrideId(null); setOverrideReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Override Verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Set the verification status for debt #{verifyOverrideId} and optionally provide a reason for the override.</p>
            <div className="space-y-1.5">
              <Label htmlFor="override-reason">Reason (optional)</Label>
              <Input
                id="override-reason"
                placeholder="e.g. Document verified by phone call"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { setVerifyOverrideId(null); setOverrideReason(""); }}>Cancel</Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
              disabled={verifyOverrideMutation.isPending}
              onClick={() => { if (verifyOverrideId) verifyOverrideMutation.mutate({ id: verifyOverrideId, verificationStatus: "flagged", reason: overrideReason || undefined }); }}>
              {verifyOverrideMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Flag className="h-3.5 w-3.5 mr-1" />}
              Flag
            </Button>
            <Button size="sm" className="text-green-700 bg-green-100 border-green-200 hover:bg-green-200 border"
              disabled={verifyOverrideMutation.isPending}
              onClick={() => { if (verifyOverrideId) verifyOverrideMutation.mutate({ id: verifyOverrideId, verificationStatus: "verified", reason: overrideReason || undefined }); }}>
              {verifyOverrideMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
              Mark Verified
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDebtId !== null} onOpenChange={(open) => { if (!open) { setRejectDebtId(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Debt Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Provide a reason for rejecting debt #{rejectDebtId}. The applicant may see this reason.</p>
            <div className="space-y-1.5">
              <Label htmlFor="reject-reason">Reason <span className="text-red-500">*</span></Label>
              <Input
                id="reject-reason"
                placeholder="e.g. Document does not match application details"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setRejectDebtId(null); setRejectReason(""); }}>Cancel</Button>
            <Button size="sm" variant="destructive"
              disabled={!rejectReason.trim() || updateDebtMutation.isPending}
              onClick={() => {
                if (rejectDebtId) {
                  updateDebtMutation.mutate({ id: rejectDebtId, status: "rejected", reason: rejectReason.trim() });
                  setRejectDebtId(null);
                  setRejectReason("");
                }
              }}>
              {updateDebtMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuoteCarousel />

      {versionData !== undefined && (
        <footer className="max-w-6xl mx-auto px-4 py-3 flex justify-end">
          <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-0.5 rounded">
            {versionData.version ? `v${versionData.version.slice(0, 7)}` : "vunknown"}
          </span>
        </footer>
      )}
      <AiAssistantPanel suggestions={aiSuggestions} onNavigate={navigate} />
    </div>
  );
}
